import { useCustomTicketsMutation } from '@/store/surveys/surveys.api';
import useCallApiProcess from '../useCallApiProcess';
import { useSelector } from 'react-redux';
import { useEvent, useSurveys, useAuth, useSummary, useBooking } from '@/store';
import { useSaveBookingSurveyMutation } from '@/store/surveys/surveys.api';
import useStepper from './useStepper';
import isEmpty from 'lodash/isEmpty';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import useCheckoutPayBooking from './useCheckoutPayBooking';
import { questionsByMerch } from '@/config/onDemandMerchConfig';
import useUserTracking from '@/hooks/userTracking';

export default function useCheckoutCustomTickets() {
  const {
    event: {
      event: { id },
    },
  } = useEvent();
  const surveys = useSelector((state) => state.surveys);
  const booking = useSelector((state) => state.booking);
  const ticketInsurance = useSelector((state) => state.addons.ticketInsurance);
  const { summary } = useSummary();
  const { surveySaved } = useUserTracking();

  const { questionsByID } = useSurveys();
  const { nextStep, jumpTo, success } = useStepper();
  const { saveSurvey, saveCustomTickets } = useAnalyticsEvents();
  const { auth, isSeller } = useAuth();
  const { booking: bookingData } = useBooking();

  const [customTicketsRequest] = useCustomTicketsMutation();
  const { callApi: callCustomTickets } =
    useCallApiProcess(customTicketsRequest);

  const [saveSurveyRequest] = useSaveBookingSurveyMutation();
  const { callApi: callSaveSurvey } = useCallApiProcess(saveSurveyRequest);

  const { payFreeOrder } = useCheckoutPayBooking();

  const customizeTicketsNames = async () => {
    let survey = [];
    let custom = surveys.custom || {};
    Object.keys(custom).map((key) => {
      if (custom[key] && custom[key].name) {
        survey = [
          ...survey,
          {
            ticket_id: key,
            full_name: custom[key].name,
          },
        ];
      }
    });
    const body = {
      event_id: id,
      booking_number: booking.order,
      email: auth.user.email,
      tickets: survey,
    };

    if (survey.length > 0) {
      saveCustomTickets(body);
      return callCustomTickets(body);
    }

    return {};
  };

  const saveAnswers = async () => {
    let survey = {};
    if (surveys.booking) {
      let questionsByBooking = [];
      Object.keys(surveys.booking).map((key) => {
        const currentQuestion = questionsByID[key.replace('q-', '')];
        questionsByBooking = [
          ...questionsByBooking,
          {
            question_id: currentQuestion.id,
            question_name: currentQuestion.label,
            answer: surveys.booking[key],
          },
        ];
      });
      if (questionsByBooking.length > 0) {
        survey = {
          questions_by_booking: questionsByBooking,
        };
      }
    }
    if (surveys.tickets) {
      let questionsByTickets = {};
      Object.keys(surveys.tickets).map((ticketID) => {
        let questionsCurrentTickets = [];
        Object.keys(surveys.tickets[ticketID]).map((key) => {
          if (key !== 'name') {
            const currentQuestion = questionsByID[key.replace('q-', '')];
            questionsCurrentTickets = [
              ...questionsCurrentTickets,
              {
                question_id: currentQuestion.id,
                question_name: currentQuestion.label,
                answer: surveys.tickets[ticketID][key],
              },
            ];
          }
        });
        if (questionsCurrentTickets.length > 0) {
          questionsByTickets = {
            ...questionsByTickets,
            [ticketID]: questionsCurrentTickets,
          };
        }
      });
      if (!isEmpty(questionsByTickets)) {
        survey = {
          ...survey,
          questions_by_ticket: questionsByTickets,
        };
      }
    }
    if (!isEmpty(survey)) {
      const body = {
        event: id,
        booking: booking?.orderInfo?.id,
        preRegister: bookingData?.registerId,
        answers: survey,
      };
      saveSurvey(body);
      surveySaved();
      return callSaveSurvey(body);
    }

    return {};
  };

  const saveMerchAnswers = async () => {
    let questionsId = {};

    Object.values(questionsByMerch).map((q) => {
      questionsId = {
        ...questionsId,
        [`${q.id}`]: q,
      };
    });

    let survey = {};
    if (surveys.merch) {
      let questionsByMerch = [];
      Object.keys(surveys.merch).map((key) => {
        const currentQuestion = questionsId[key.replace('q-', '')];
        questionsByMerch = [
          ...questionsByMerch,
          {
            question_id: currentQuestion.id,
            question_name: currentQuestion.label,
            answer: surveys.merch[key],
          },
        ];
      });
      if (questionsByMerch.length > 0) {
        survey = {
          questions_by_booking: questionsByMerch,
        };
      }
    }
    if (!isEmpty(survey)) {
      const body = {
        event: id,
        booking: bookingData?.orderInfo?.id,
        answers: survey,
      };
      saveSurvey(body);
      surveySaved();
      return callSaveSurvey(body);
    }

    return {};
  };

  const saveSuffleAnswers = async () => {
    let survey = {};
    let ticketNames = [];
    let bodySurveys, bodyNames;

    if (surveys.booking) {
      let questionsByBooking = [];
      Object.keys(surveys.booking).map((key) => {
        const currentQuestion = questionsByID[key.replace('q-', '')];
        questionsByBooking = [
          ...questionsByBooking,
          {
            question_id: currentQuestion.id,
            question_name: currentQuestion.label,
            answer: surveys.booking[key],
          },
        ];
      });
      if (questionsByBooking.length > 0) {
        survey = {
          questions_by_booking: questionsByBooking,
        };
      }
    }
    if (surveys.tickets) {
      let questionsByTickets = {};
      Object.keys(surveys.tickets).map((ticketID) => {
        let questionsCurrentTickets = [];
        Object.keys(surveys.tickets[ticketID]).map((key) => {
          if (key !== 'name') {
            const currentQuestion = questionsByID[key.replace('q-', '')];
            let currentAnswer = surveys.tickets[ticketID][key];
            if (currentQuestion?.field_type === 'phone') {
              currentAnswer = currentAnswer.replace(' ', '');
            }
            questionsCurrentTickets = [
              ...questionsCurrentTickets,
              {
                question_id: currentQuestion.id,
                question_name: currentQuestion.label,
                answer: currentAnswer,
              },
            ];
          } else {
            ticketNames.push({
              ticket_id: ticketID,
              full_name: surveys.tickets[ticketID][key],
            });
          }
        });
        if (questionsCurrentTickets.length > 0) {
          questionsByTickets = {
            ...questionsByTickets,
            [ticketID]: questionsCurrentTickets,
          };
        }
      });
      if (!isEmpty(questionsByTickets)) {
        survey = {
          ...survey,
          questions_by_ticket: questionsByTickets,
        };
      }
    }

    if (!isEmpty(ticketNames)) {
      bodyNames = {
        event_id: id,
        booking_number: booking.order,
        email: auth.user.email,
        tickets: ticketNames,
      };
    }

    if (!isEmpty(survey)) {
      bodySurveys = {
        event: id,
        booking: booking.orderInfo.id,
        answers: survey,
      };
    }

    return { bodyNames, bodySurveys };
  };

  const saveSurveys = async (surveysBody) => {
    saveSurvey(surveysBody);
    surveySaved();
    return callSaveSurvey(surveysBody);
  };

  const saveNames = async (namesBody) => {
    saveCustomTickets(namesBody);
    return callCustomTickets(namesBody);
  };

  const next = async (skipSurvey) => {
    if (skipSurvey) {
      jumpTo('SUCCESS');
      return;
    }
    if (summary?.total_to_pay === 0 && !isSeller) {
      const resp = await payFreeOrder();
      if (resp.error) return;

      jumpTo('SUCCESS');
      return;
    }
    nextStep();
  };

  const customTickets = async (isPreRegister) => {
    const customizeTicketsResponse = (await isPreRegister)
      ? Promise.resolve()
      : customizeTicketsNames();
    const saveAnswersResponse = await saveAnswers();

    if (!customizeTicketsResponse?.error && !saveAnswersResponse?.error) {
      next(isPreRegister);
    }
  };

  const suffleCustom = async () => {
    const { bodyNames, bodySurveys } = await saveSuffleAnswers();

    let surveysResponse = bodySurveys
      ? await saveSurveys(bodySurveys)
      : () => {};
    let namesResponse = bodyNames ? await saveNames(bodyNames) : () => {};

    if (!surveysResponse.error && !namesResponse.error) {
      next();
    }
  };

  const customMerch = async () => {
    const saveMerchAnswersResponse = await saveMerchAnswers();

    if (!saveMerchAnswersResponse.error) {
      next();
    }
  };

  return {
    customTickets,
    customMerch,
    suffleCustom,
  };
}
